/** Case-insensitive substring filter over branch names; blank query keeps all. */
export function filterBranches(branches: readonly string[], query: string): readonly string[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return branches;
  return branches.filter((branch) => branch.toLowerCase().includes(needle));
}

/** Which side of the local | remote toggle a branch picker is showing. */
export type BranchScope = 'local' | 'remote';

/** The inputs a branch picker filters and orders for the active scope. */
export type BranchPickerSource = {
  localBranches: readonly string[];
  remoteBranches: readonly string[];
  currentBranch: string;
  defaultBranch: string | null;
};

/**
 * The ordered branch list a picker renders for `scope`: filter the scope's list
 * by `query`, then pin the repository default branch first and the current
 * checkout second. A branch that is both default and current is only listed
 * once.
 */
export function pickerBranches(
  scope: BranchScope,
  source: BranchPickerSource,
  query: string,
): string[] {
  const list = scope === 'remote' ? source.remoteBranches : source.localBranches;
  return pinBranches(filterBranches(list, query), [source.defaultBranch, source.currentBranch]);
}

/** Moves the given names to the front, keeping the original order of everything else. */
export function pinBranches(
  branches: readonly string[],
  pinned: readonly (string | null | undefined)[],
): string[] {
  const seen = new Set<string>();
  const leading: string[] = [];
  for (const pin of pinned) {
    const name = pin?.trim() ?? '';
    if (name.length === 0 || seen.has(name) || !branches.includes(name)) continue;
    seen.add(name);
    leading.push(name);
  }
  return [...leading, ...branches.filter((branch) => !seen.has(branch))];
}
